import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "dropzone", "preview", "filename", "filesize", "progress", "progressBar", "removeBtn"]
  static values = {
    acceptedTypes: { type: Array, default: ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"] },
    maxSize: { type: Number, default: 5242880 } // 5MB in bytes
  }

  connect() {
    this.bindEvents()
  }

  bindEvents() {
    // Only bind events if dropzone exists (it won't exist if logo is already attached)
    if (!this.hasDropzoneTarget) {
      return
    }

    // Prevent default drag behaviors on the whole document
    ;["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.preventDefaults.bind(this), false)
    })

    // Highlight drop zone when dragging over it
    ;["dragenter", "dragover"].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.highlight.bind(this), false)
    })

    ;["dragleave", "drop"].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.unhighlight.bind(this), false)
    })

    // Handle dropped files
    this.dropzoneTarget.addEventListener("drop", this.handleDrop.bind(this), false)
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight() {
    this.dropzoneTarget.classList.add("border-primary-500", "bg-primary-50")
    this.dropzoneTarget.classList.remove("border-gray-300")
  }

  unhighlight() {
    this.dropzoneTarget.classList.remove("border-primary-500", "bg-primary-50")
    this.dropzoneTarget.classList.add("border-gray-300")
  }

  handleDrop(e) {
    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.processFile(files[0])
    }
  }

  handleFileSelect(e) {
    const files = e.target.files
    if (files.length > 0) {
      this.processFile(files[0])
    }
  }

  triggerFileInput() {
    this.inputTarget.click()
  }

  processFile(file) {
    // Validate file type
    if (!this.acceptedTypesValue.includes(file.type)) {
      this.showError("Please upload a PNG, JPEG, GIF, or WebP image.")
      return
    }

    // Validate file size
    if (file.size > this.maxSizeValue) {
      this.showError("File must be less than 5MB.")
      return
    }

    // Show preview immediately
    this.showPreview(file)

    // Start direct upload
    this.uploadFile(file)
  }

  showPreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (this.hasPreviewTarget) {
        this.previewTarget.src = e.target.result
        this.previewTarget.classList.remove("hidden")
      }
    }
    reader.readAsDataURL(file)

    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = file.name
    }

    if (this.hasFilesizeTarget) {
      this.filesizeTarget.textContent = this.formatFileSize(file.size)
    }

    // Show progress area, hide dropzone
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
    }
    this.dropzoneTarget.classList.add("hidden")
  }

  uploadFile(file) {
    const url = this.inputTarget.dataset.directUploadUrl
    const upload = new DirectUpload(file, url, this)

    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        this.resetDropzone()
      } else {
        // Create hidden input with the signed blob id
        this.createHiddenInput(blob)
        this.uploadComplete()
      }
    })
  }

  // DirectUpload delegate methods
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => this.updateProgress(event))
  }

  updateProgress(event) {
    const progress = (event.loaded / event.total) * 100
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
    }
  }

  uploadComplete() {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "100%"
    }

    // Show remove button after successful upload
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.remove("hidden")
    }
  }

  createHiddenInput(blob) {
    const inputName = this.inputTarget.name

    // Remove any existing hidden input for this field
    const existingInput = this.element.querySelector(`input[type="hidden"][name="${inputName}"]`)
    if (existingInput) {
      existingInput.remove()
    }

    const hiddenField = document.createElement("input")
    hiddenField.type = "hidden"
    hiddenField.name = inputName
    hiddenField.value = blob.signed_id
    hiddenField.dataset.directUploadTarget = "hiddenInput"
    this.element.appendChild(hiddenField)
  }

  removeFile(e) {
    e.preventDefault()

    // Remove hidden input using the same name as the file input
    const inputName = this.inputTarget.name
    const hiddenInput = this.element.querySelector(`input[type="hidden"][name="${inputName}"]`)
    if (hiddenInput) {
      hiddenInput.remove()
    }

    // Reset the file input
    this.inputTarget.value = ""

    // Reset UI
    this.resetDropzone()
  }

  resetDropzone() {
    this.dropzoneTarget.classList.remove("hidden")

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add("hidden")
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "0%"
    }

    if (this.hasPreviewTarget) {
      this.previewTarget.src = ""
      this.previewTarget.classList.add("hidden")
    }

    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.add("hidden")
    }
  }

  showError(message) {
    if (window.showToast) {
      window.showToast(message, 'error')
    } else {
      alert(message)
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }
}
